import { motion } from 'framer-motion'
import { LCARSButton, LCARSBar, HolographicLogo, ScanLine } from '@enterpress/holomatrix'
import { Rocket, Globe, Cpu, Users } from 'lucide-react'
import { trackCTAClick } from '../lib/analytics'
import { useTranslation } from '../i18n/index'

/**
 * Hero Section - Vision Statement
 * 
 * Bold declaration positioning EnterPress as the future of WordPress
 * "WordPress Powers the Internet. EnterPress Powers WordPress."
 */
export function Hero() {
  const { t } = useTranslation()
  const scrollToWaitlist = () => {
    trackCTAClick('Join the Fleet', 'Hero')
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })
  }

  const scrollToPreview = () => {
    trackCTAClick('Join the Preview', 'Hero')
    document.getElementById('preview')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center px-6 py-20 overflow-hidden">
      {/* Scan line effect for holographic ambiance */}
      <ScanLine direction="horizontal" duration={8} opacity={0.15} />

      {/* Decorative LCARS bars */}
      <div className="absolute top-32 left-8 hidden lg:flex flex-col gap-2">
        <LCARSBar color="amber" size="lg" className="w-32" />
        <LCARSBar color="purple" size="md" className="w-24" />
        <LCARSBar color="cyan" size="sm" className="w-16" />
      </div>
      
      <div className="absolute top-32 right-8 hidden lg:flex flex-col gap-2 items-end">
        <LCARSBar color="cyan" size="lg" className="w-32" />
        <LCARSBar color="amber" size="md" className="w-24" />
        <LCARSBar color="purple" size="sm" className="w-16" />
      </div>

      <div className="max-w-5xl mx-auto text-center">
        {/* Animated Holographic Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="mb-8 flex justify-center"
        >
          <HolographicLogo size={100} />
        </motion.div>

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,212,255,0.12)] border border-[rgba(0,212,255,0.4)] text-[#00D4FF] text-sm font-semibold tracking-wider uppercase">
            <Globe size={16} />
            {t('hero.badge')}
          </span>
        </motion.div>

        {/* Main headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
        >
          <span className="text-white">{t('hero.headlineMain')}</span>
          <br />
          <span className="bg-gradient-to-r from-[#00D4FF] via-[#7B68EE] to-[#FF9900] bg-clip-text text-transparent">
            {t('hero.headlineSub')}
          </span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-xl md:text-2xl text-[rgba(232,244,248,0.7)] mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          {t('hero.descriptionPart1')}{' '}
          <span className="bg-gradient-to-r from-[#00D4FF] to-[#FFB347] bg-clip-text text-transparent font-semibold">
            {t('hero.descriptionHighlight')}
          </span>
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <LCARSButton 
            color="cyan" 
            size="lg" 
            onClick={scrollToPreview}
            className="min-w-[200px]"
          >
            <Rocket size={18} />
            {t('hero.ctaPreview')}
          </LCARSButton>
          
          <LCARSButton 
            color="amber" 
            size="lg" 
            onClick={scrollToWaitlist}
            className="min-w-[200px]"
          >
            <Rocket size={18} />
            {t('hero.ctaFleet')}
          </LCARSButton>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-20 flex flex-wrap justify-center gap-8 md:gap-16"
        >
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Globe size={20} className="text-[#00D4FF]" />
              <div className="text-3xl md:text-4xl font-bold text-[#00D4FF]">43%</div>
            </div>
            <div className="text-sm text-[rgba(232,244,248,0.5)] uppercase tracking-wider">{t('hero.statsWeb')}</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Cpu size={20} className="text-[#FFB347]" />
              <div className="text-3xl md:text-4xl font-bold text-[#FFB347]">{t('hero.statsEnterprise')}</div>
            </div>
            <div className="text-sm text-[rgba(232,244,248,0.5)] uppercase tracking-wider">{t('hero.statsInfra')}</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Users size={20} className="text-[#9D8CFF]" />
              <div className="text-3xl md:text-4xl font-bold text-[#9D8CFF]">{t('hero.statsAgentic')}</div>
            </div>
            <div className="text-sm text-[rgba(232,244,248,0.5)] uppercase tracking-wider">{t('hero.statsOps')}</div>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-[rgba(0,212,255,0.4)] flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-3 rounded-full bg-[#00D4FF]" />
        </motion.div>
      </motion.div>
    </section>
  )
}
